
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";

const resetSchema = z.object({
  email: z.string().email("Formato de email inválido")
});

type PasswordResetFormProps = {
  onBackToLogin: () => void;
};

export const PasswordResetForm = ({
  onBackToLogin
}: PasswordResetFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLanguage();
  const { getPath } = useLanguagePath();
  
  const form = useForm<z.infer<typeof resetSchema>>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      email: ""
    }
  });
  
  const onSubmit = async (values: z.infer<typeof resetSchema>) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
        redirectTo: window.location.origin + getPath('/reset-password')
      });
      
      if (error) {
        console.error("Erro ao solicitar redefinição de senha:", error);
        toast.error(t('auth.reset.errorMessage'));
        return;
      }
      
      toast.success(t('auth.reset.successMessage'));
      onBackToLogin();
    } catch (error: any) {
      console.error("Erro inesperado ao solicitar redefinição de senha:", error);
      toast.error(t('auth.reset.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center space-y-4">
        <img src="/lovable-uploads/e79feb48-6e7c-488a-8626-bb79f4bde67a.png" alt="Logo" className="h-20 w-auto" />
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight dark:text-white">
            {t('auth.reset.title')}
          </h1>
          <p className="text-sm text-muted-foreground dark:text-gray-400">
            {t('auth.reset.subtitle')}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="dark:text-gray-300">{t('auth.reset.emailLabel')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      {...field}
                      type="email"
                      className="pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder={t('auth.reset.emailPlaceholder')}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('auth.reset.submitting') : t('auth.reset.submitButton')}
          </Button>
        </form>
      </Form>

      <div className="text-center">
        <button
          onClick={onBackToLogin}
          type="button" 
          className="text-sm text-primary dark:text-white hover:underline"
        >
          {t('auth.reset.backToLogin')}
        </button>
      </div>
    </div>
  );
};
